import _ from 'underscore';
import React, { useCallback, useContext, useMemo, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { LocationTargeting, targetOptions } from '@bingads-webui-campaign-react/location-targeting';
import {
  isLegacyLocationAndRadiusEqual,
  BingMapsContextProvider,
  getIsRadiusTarget as defaultGetIsRadiusTarget,
  TARGETING_TYPE,
  SEARCH_TYPE,
  LocationTarget,
} from '@bingads-webui-react/bing-maps';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { useSearchLocationData } from '@bingads-webui-campaign-react/pmax-lite-data';

import { getLocationTargetsEditorStrings } from '../utils/common';

const BING_MAPS_API_KEY = 'AgRuCtepgsuuCYV9J6S6w1j8ay2JJwnu8f5EPgv4kkoJoIuka8yui-NzKrvlESTQ';

export const TargetingLocation = ({
  data,
  onDataChange,
  quickOption,
  logger,
  isLoading,
}) => {
  const mapRef = useRef();
  const i18n = useContext(I18nContext);
  const { scope } = useContext(ScopeContext);
  const { cid, aid } = scope.values;

  const {
    isPeopleInLocationChecked = true,
    isPeopleSearchingForLocationChecked = true,
    selectedLocationOption,
    negativeLocationTargets = [],
    positiveLocationTargets = [],
  } = data;

  const serverContext = useContext(ServerContext);
  const { getLocation } = useSearchLocationData(aid, cid);

  // Save location targets selected under specific location option
  const [tempLocationTargets, setTempLocationTargets] = useState({
    positiveLocationTargets,
    negativeLocationTargets,
  });

  const locationTargetsEditorStrings = useMemo(() => (getLocationTargetsEditorStrings(i18n)), [i18n]);

  const optionConfigs = useMemo(() => ([
    {
      key: targetOptions.quick,
      // eslint-disable-next-line max-len
      text: quickOption.location && (i18n.getString(quickOption.location.ExpandedName) || quickOption.location.ExpandedName),
    },
    targetOptions.everywhere,
    targetOptions.specific,
  ]), [quickOption]);

  const onPositiveLocationTargetsChange = useCallback((newTargets) => {
    onDataChange({ positiveLocationTargets: newTargets });
    setTempLocationTargets(prev => ({
      ...prev,
      positiveLocationTargets: newTargets,
    }));
  }, [onDataChange]);

  const onNegativeLocationTargetsChange = useCallback((newTargets) => {
    onDataChange({ negativeLocationTargets: newTargets });
    setTempLocationTargets(prev => ({
      ...prev,
      positiveLocationTargets: newTargets,
    }));
  }, [onDataChange]);

  const onPeopleInLocationChange = useCallback((checked) => {
    onDataChange({
      isPeopleInLocationChecked: checked,
    });
  }, [onDataChange]);

  const onPeopleSearchingForLocationChange = useCallback((checked) => {
    onDataChange({
      isPeopleSearchingForLocationChecked: checked,
    });
  }, [onDataChange]);

  const onSelectedLocationOptionChange = useCallback((newOption) => {
    if (newOption === targetOptions.quick) {
      onDataChange({
        selectedLocationOption: newOption,
        positiveLocationTargets: [quickOption],
        locationsErrorString: null,
        isPeopleInLocationChecked: true,
        isPeopleSearchingForLocationChecked: true,
      });
    } else if (newOption === targetOptions.specific) {
      onDataChange({
        ...tempLocationTargets,
        selectedLocationOption: newOption,
        isPeopleInLocationChecked: true,
        isPeopleSearchingForLocationChecked: true,
      });
    } else if (newOption === targetOptions.everywhere) {
      onDataChange({
        positiveLocationTargets: [],
        negativeLocationTargets: [],
        selectedLocationOption: newOption,
        isPeopleInLocationChecked: true,
        isPeopleSearchingForLocationChecked: true,
      });
    }
  }, [onDataChange, quickOption, tempLocationTargets]);

  const onValidationComplete = useCallback((errorMessage) => {
    onDataChange({
      locationsErrorString: errorMessage,
    });
  }, [onDataChange]);

  const getIsRadiusTarget = useCallback((location, targetingType) => {
    if (targetingType) {
      return defaultGetIsRadiusTarget(location, targetingType) &&
        targetingType === TARGETING_TYPE.Included;
    }
    return defaultGetIsRadiusTarget(location, targetingType);
  }, []);

  const searchOptions = useMemo(() => ({
    getLocations: search => getLocation(search).then((result) => {
      logger.trace(`search location "${search}": ${JSON.stringify(result)}`);
      return result;
    }).catch((err) => {
      logger.error(`search location "${search}" with error: ${JSON.stringify(err)}`);
      throw new Error(err);
    }),
  }), [getLocation, logger]);

  useEffect(() => {
    if (
      selectedLocationOption === targetOptions.specific
      && (
        tempLocationTargets.positiveLocationTargets.length !== positiveLocationTargets.length
        || tempLocationTargets.negativeLocationTargets.length !== negativeLocationTargets.length
      )
    ) {
      setTempLocationTargets({
        positiveLocationTargets,
        negativeLocationTargets,
      });
    }
  }, [negativeLocationTargets, positiveLocationTargets, selectedLocationOption, tempLocationTargets]);

  return (
    <BingMapsContextProvider
      campaignApiToken={serverContext.Token}
      bingMapsApiKey={BING_MAPS_API_KEY}
      searchOptions={searchOptions}
    >
      <LocationTargeting
        isLoading={isLoading}
        allowPositiveLocationTargeting
        allowNegativeLocationTargeting
        showSearchTypeOptions
        strings={locationTargetsEditorStrings}
        mapRef={mapRef}
        defaultSelectedSearchType={SEARCH_TYPE.Boundary}
        isRadiusExcludeEnabled={false}
        maxLocationTargets={1000}
        maxNegativeLocationTargets={1000}
        customizedLocationEqual={isLegacyLocationAndRadiusEqual}
        getIsRadiusTarget={getIsRadiusTarget}

        // Pending
        locationTargets={positiveLocationTargets}
        negativeLocationTargets={negativeLocationTargets}
        onLocationTargetsChange={onPositiveLocationTargetsChange}
        onNegativeLocationTargetsChange={onNegativeLocationTargetsChange}
        useInternalValidation={false}
        onNotifyValidationResult={onValidationComplete}

        // ChoiceGroups
        optionsConfig={optionConfigs}
        selectedLocationOption={selectedLocationOption}
        // selectedLocationOption="specific"
        onChangeLocationOption={onSelectedLocationOptionChange}

        // Hide intent during signup as required, doesn't remove the logic
        // related as it is well implemented and tested, we can bring it back if needed.
        shouldShowTargetingIntent={false}
        isPeopleInLocationChecked={isPeopleInLocationChecked}
        isPeopleSearchingForLocationChecked={isPeopleSearchingForLocationChecked}
        onPeopleInLocationChange={onPeopleInLocationChange}
        onPeopleSearchingForLocationChange={onPeopleSearchingForLocationChange}
      />
    </BingMapsContextProvider>
  );
};

TargetingLocation.propTypes = {
  data: PropTypes.shape({
    isPeopleInLocationChecked: PropTypes.bool,
    isPeopleSearchingForLocationChecked: PropTypes.bool,
    selectedLocationOption: PropTypes.string,
    negativeLocationTargets: PropTypes.arrayOf(PropTypes.instanceOf(LocationTarget)),
    positiveLocationTargets: PropTypes.arrayOf(PropTypes.instanceOf(LocationTarget)),
  }),
  onDataChange: PropTypes.func,
  quickOption: PropTypes.instanceOf(LocationTarget),
  logger: PropTypes.shape({
    trace: PropTypes.func.isRequired,
    error: PropTypes.func.isRequired,
  }),
  isLoading: PropTypes.bool,
};

TargetingLocation.defaultProps = {
  data: {
    isPeopleInLocationChecked: true,
    isPeopleSearchingForLocationChecked: true,
    selectedLocationOption: targetOptions.specific,
    negativeLocationTargets: [],
    positiveLocationTargets: [],
  },
  onDataChange: _.noop,
  quickOption: {},
  logger: {
    trace: _.noop,
    error: _.noop,
  },
  isLoading: false,
};
